import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Bell, ChevronDown, LogOut, Menu, Smartphone, User } from 'lucide-react';
import { cn, formatSmartDateTime } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';
import { useResolvedRange } from '@/store/dateFilterStore';
import { roleLabel } from '@/types';
import { IconButton } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Misc';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { Skeleton } from '@/components/ui/Feedback';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { useRecentActivities } from '@/hooks/useDashboard';

export function Topbar({
  title,
  subtitle,
  showDateFilter = false,
  onMenuClick,
  onCollapseToggle,
}: {
  title: string;
  subtitle?: string;
  /** Only routes whose data is date-scoped surface the filter. */
  showDateFilter?: boolean;
  onMenuClick: () => void;
  onCollapseToggle: () => void;
}) {
  return (
    <header className="sticky top-0 z-20 flex min-h-[76px] items-center gap-2 border-b border-line bg-canvas/85 px-3 backdrop-blur-md sm:gap-3 sm:px-4 lg:h-[86px] lg:px-7">
      <IconButton label="Open menu" onClick={onMenuClick} className="lg:hidden">
        <Menu className="size-5" aria-hidden />
      </IconButton>
      <IconButton label="Toggle sidebar" onClick={onCollapseToggle} className="hidden lg:inline-flex">
        <Menu className="size-5" aria-hidden />
      </IconButton>

      <div className="min-w-0 flex-1">
        <h1 className="truncate text-base font-bold tracking-tight text-ink sm:text-xl">{title}</h1>
        {subtitle && <p className="hidden truncate text-[13px] text-ink-muted sm:block">{subtitle}</p>}
      </div>

      {showDateFilter && <DateRangePicker />}
      <NotificationsMenu />
      <UserMenu />
    </header>
  );
}

function NotificationsMenu() {
  const range = useResolvedRange();
  const { data: activities, isPending } = useRecentActivities(6);
  const [open, setOpen] = useState(false);
  const ref = useOutsideClick<HTMLDivElement>(() => setOpen(false), open);

  return (
    <div className="relative" ref={ref}>
      <IconButton
        label="Notifications"
        onClick={() => setOpen((value) => !value)}
        variant="secondary"
        className="relative"
      >
        <Bell className="size-[18px]" aria-hidden />
        {activities && activities.length > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {activities.length}
          </span>
        )}
      </IconButton>

      {open && (
        // Clamped to the viewport so the panel never hangs off a 360px screen.
        <div className="animate-rise absolute right-0 z-30 mt-2 w-[min(20rem,calc(100vw-1.5rem))] overflow-hidden rounded-xl border border-line bg-white shadow-pop">
          <div className="border-b border-line px-4 py-3">
            <p className="text-sm font-bold text-ink">Recent activity</p>
            {range.active && (
              <p className="mt-0.5 truncate text-[11px] text-ink-muted">{range.longLabel}</p>
            )}
          </div>
          <ul className="max-h-[min(20rem,60vh)] overflow-y-auto">
            {isPending &&
              Array.from({ length: 4 }, (_, index) => (
                <li key={index} className="flex flex-col gap-2 border-b border-line/70 px-4 py-3 last:border-0">
                  <Skeleton className="h-3.5 w-32" />
                  <Skeleton className="h-3 w-48" />
                </li>
              ))}

            {!isPending &&
              (activities ?? []).map((activity) => (
                <li key={activity.id} className="border-b border-line/70 px-4 py-3 last:border-0">
                  <p className="text-[13px] font-semibold text-ink">
                    {activity.kind === 'QR_GENERATED'
                      ? 'QR Generated'
                      : activity.kind === 'RECEIVED'
                        ? 'Cartons Received'
                        : 'Cartons Dispatched'}
                  </p>
                  <p className="mt-0.5 text-xs text-ink-muted">
                    {activity.poNumber} · {activity.cartons} cartons · {activity.byUserName}
                  </p>
                  <p className="mt-0.5 text-[11px] text-ink-muted">{formatSmartDateTime(activity.at)}</p>
                </li>
              ))}

            {!isPending && (!activities || activities.length === 0) && (
              <li className="px-4 py-6 text-center text-[13px] text-ink-muted">
                {range.active ? 'No activity in this date range.' : 'Nothing new today.'}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const ref = useOutsideClick<HTMLDivElement>(() => setOpen(false), open);

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-2.5 rounded-xl px-1 py-1.5 transition-colors hover:bg-white sm:px-1.5"
      >
        <Avatar name={user.name} src={user.avatarUrl} />
        <span className="hidden text-left lg:block">
          <span className="block text-[13px] leading-tight font-bold text-ink">{user.name}</span>
          <span className="block text-[11px] text-ink-muted">{roleLabel(user.role, user.roleName)}</span>
        </span>
        <ChevronDown
          className={cn('hidden size-4 text-ink-muted transition-transform lg:block', open && 'rotate-180')}
          aria-hidden
        />
      </button>

      {open && (
        <div
          role="menu"
          className="animate-rise absolute right-0 z-30 mt-2 w-[min(15.5rem,calc(100vw-1.5rem))] overflow-hidden rounded-xl border border-line bg-white shadow-pop"
        >
          <div className="border-b border-line px-4 py-3">
            <p className="text-sm font-bold text-ink">{user.name}</p>
            <p className="truncate text-xs text-ink-muted">{user.email}</p>
          </div>

          <Link
            to="/settings"
            onClick={() => setOpen(false)}
            role="menuitem"
            className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] font-medium text-ink-soft transition-colors hover:bg-canvas"
          >
            <User className="size-4" aria-hidden />
            My profile
          </Link>

          <Link
            to="/m"
            onClick={() => setOpen(false)}
            role="menuitem"
            className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] font-medium text-ink-soft transition-colors hover:bg-canvas"
          >
            <Smartphone className="size-4" aria-hidden />
            Open scanner app
          </Link>

          <button
            type="button"
            role="menuitem"
            disabled={signingOut}
            onClick={async () => {
              setSigningOut(true);
              await logout();
              navigate('/login', { replace: true });
            }}
            className="flex w-full items-center gap-2.5 border-t border-line px-4 py-2.5 text-[13px] font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60"
          >
            <LogOut className={cn('size-4', signingOut && 'animate-spin')} aria-hidden />
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      )}
    </div>
  );
}
